import GsuLoader from '@/components/GsuLoader'
import { PageMetaInfo } from '@/compositions/MetaInfo'
import { FragPageMetaInfo } from '@/compositions/MetaInfo/query'
import { HandleOnLoadAction } from '@/compositions/OnLoadAction'
import { FragOnLoadAction } from '@/compositions/OnLoadAction/query'
import type { Props as PageComposerProps } from '@/compositions/PageComposer'
import { FragComposerSections } from '@/compositions/PageComposer/query'
import PageLayout from '@/compositions/PageLayout'
import { isPartnerInfoSection } from '@/compositions/PartnerInfoBlock'
import { useFeatureToggle } from '@/context/featureToggles'
import { useWelcomePopup } from '@/hooks/useWelcomePopup'
import { getConfig } from '@/lib/config'
import { getPartnerMetadataFromCookieUsingPartnerId } from '@/lib/partners'
import { queryGsuApi, QUERY_PARTNER_ASSOCIATIONS } from '@/service/query'
import { getClientIdentityForQuery, getPartnerAttributionStateFromCookies } from '@/service/request'
import { CurrentUser, MetaInfo, Page, PartnerInfoPageSection } from '@/shared/api-generated-types'
import AccessRestrictedDialog from '@/widgets/AccessRestrictedDialog'
import Modal from '@/widgets/Modal'
import UserPreferencesWrapper from '@/widgets/UserPreferencesModal/wrapper'
import WelcomeContent from '@/widgets/WelcomeContent'
import WelcomePopupContent from '@/widgets/WelcomePopupContent'
import axios from 'axios'
import { intersection, keysIn } from 'lodash'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import omitBy from 'lodash/omitBy'
import toString from 'lodash/toString'
import type { GetServerSideProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import * as s from 'superstruct'
import { gql, useQuery } from 'urql'

const { publicRuntimeConfig } = getConfig()

const QUERY_PARTNER = gql`
  query Partner($slug: String!) {
    ${FragComposerSections}
    ${FragPageMetaInfo}
    ${FragOnLoadAction}

    currentUser {
      authenticatedUser {
        id
      }
    }

    page: partnerPage(slug: $slug) {
      title
      id
      metaInfo {
        ...PageMetaInfo
      }
      onLoadAction {
        ...OnLoadAction
      }
      sections {
        ...CoreSections
        ...RecursiveSections
      }
    }
  }
`

const QUERY_SECTIONS = gql`
  ${FragComposerSections}
  query Sections($slug: String!) {
    sections: partnerPageSection(slug: $slug) {
      sections {
        ...CoreSections
        ...RecursiveSections
      }
    }
  }
`

const QUERY_PAGE_ONLOAD_ACTION_AND_SOFT_ATTRIBUTE = gql`
  ${FragOnLoadAction}

  query OnLoadActionAndSoftAttributeQuery($slug: String!) {
    page: partnerPage(slug: $slug) {
      id
      catalogueLanguage
      title
      onLoadAction {
        ...OnLoadAction
      }
    }
  }
`

const QUERY_SET_PARTNER_METADATA = gql`
  query setPartnerMetadata($input: SetPartnerMetadataInput!) {
    setPartnerMetadata(input: $input) {
      __typename
      success
    }
  }
`

const QUERY_PARTNER_BY_ID = gql`
  query PartnerById($id: ID!) {
    partner(id: $id) {
      id
      name
      useDefaultWelcomePopup
      welcomePopup {
        title
        description
        videoPoster
        videoAsset {
          playbackUrlMP4
          playbackUrlHLS
          playbackUrlDash
        }
      }
    }
  }
`

type WelcomePopupData = {
  partner: {
    id: string
    name: string
    useDefaultWelcomePopup: boolean | null
    welcomePopup: {
      title: string | null
      description: string | null
      videoPoster: string | null
      videoAsset: {
        playbackUrlMP4: string | null
        playbackUrlHLS: string | null
        playbackUrlDash: string | null
      } | null
    } | null
  } | null
}

function getPartnerIdFromPageSections(pageData: Page | undefined): string | undefined {
  if (!pageData) return
  let partnerId = undefined
  pageData.sections.forEach((section) => {
    if (section.__typename === 'PartnerInfoPageSection') {
      partnerId = section.content.id
    }
  })
  return partnerId
}

enum JoinModalSteps {
  init = 'init',
  success = 'success',
}

// TODO: Update the following two enums when new partners require auth modals
enum JoinModalAllowedPartnerRefs {
  tivity = 'tivity',
}
enum JoinModalAllowedPartnerURLSlugs {
  silversneakers = 'silversneakers',
}

const JoinModalParams = s.object({
  slug: s.enums(Object.values(JoinModalAllowedPartnerURLSlugs)),
  ref: s.enums(Object.values(JoinModalAllowedPartnerRefs)),
  step: s.enums(Object.values(JoinModalSteps)),
  simulate: s.optional(s.boolean()),
  account: s.optional(s.string()),
  utm_classid: s.optional(s.string()),
  utm_classname: s.optional(s.string()),
  utm_category: s.optional(s.string()),
  utm_series: s.optional(s.string()),
})

type JoinModalParams = s.Infer<typeof JoinModalParams>

// PageComposer dynamic import to prevent SSR hydration issues
const PageComposerWithLoader = dynamic<PageComposerProps>(() => import('@/compositions/PageComposer'), {
  ssr: false,
  loading: () => <GsuLoader consumeSpace />,
})

interface PageData {
  page: Page
  metaInfo: MetaInfo
}

interface ExtraPageData {
  sections: {
    sections: Page['sections']
  }
}

const Partner: NextPage<{
  slug: string
  joinModalParams: JoinModalParams | null
  partnerAuthURL: string | null
  partnerId: string
  authToken?: string
}> = (props) => {
  const router = useRouter()
  const { t } = useTranslation(['partner', 'series'])
  // Fetch page data
  const [pageData, setPageData] = useState<Page | undefined>(undefined)
  const canSeeHomeAdditionalCategoryRows = useFeatureToggle('exp_homepage_category_rows')
  const [showAccessRestrictedDialog, setAccessRestrictedAuthDialog] = useState<boolean>(false)
  const [checkAndCreatePartnerAssociation, setCheckAndCreatePartnerAssociation] = useState<boolean>(false)

  // Page request
  const [{ data: initialPageData, fetching }] = useQuery<PageData>({
    query: QUERY_PARTNER,
    variables: { slug: props.slug },

    requestPolicy: 'cache-first',
    pause: !props?.slug,
  })

  // Extra Page request
  const [{ data: extraPageData, fetching: fetchingExtras }] = useQuery<ExtraPageData>({
    query: QUERY_SECTIONS,
    variables: { slug: props.slug },
    requestPolicy: 'cache-first',
    pause: !props?.slug,
  })

  /**
   * ⚠️ It is important for this client-side request to be triggered.
   * This will allow GQL to clear necessary cookies.
   * The onLoadAction must still be passed into the props for the page to
   * actually *do* any onLoadAction. Such as displaying a dialog.
   *
   * ⚠️ For this page, soft attribution is also required here.
   * This request will set a partner attribution cookie, required for GSU billing.
   */
  useQuery({
    query: QUERY_PAGE_ONLOAD_ACTION_AND_SOFT_ATTRIBUTE,
    variables: { slug: props.slug },
  })

  // --- First-visit welcome popup ---
  const partnerId = getPartnerIdFromPageSections(initialPageData?.page)

  // Query partner data for welcomePopup fields (title, description, video)
  const [{ data: partnerByIdData }] = useQuery<WelcomePopupData>({
    query: QUERY_PARTNER_BY_ID,
    variables: { id: partnerId! },
    pause: !partnerId,
  })

  // localStorage-based "show once per partner" tracking for first-time visitors
  const { shouldShow: shouldShowFirstVisit, dismiss: dismissFirstVisit } = useWelcomePopup(partnerId)

  // Build first-visit popup data from welcomePopup fields
  // Only use custom welcome popup when useDefaultWelcomePopup is explicitly false
  const useDefaultWelcomePopup = partnerByIdData?.partner?.useDefaultWelcomePopup
  const welcomePopup = partnerByIdData?.partner?.welcomePopup
  const hasWelcomePopupData = useDefaultWelcomePopup === false && !!(welcomePopup?.title || welcomePopup?.description)

  // Determine which popup to show (newly-registered takes priority over first-visit)
  const hasOnLoadDialog = !!initialPageData?.page?.onLoadAction
  const showFirstVisitPopup = !hasOnLoadDialog && shouldShowFirstVisit && hasWelcomePopupData

  // State for manually opening the welcome popup via logo/sponsored-by click
  const [welcomePopupManualOpen, setWelcomePopupManualOpen] = useState(false)

  // Callback for PartnerInfoBlock: opens welcome popup if partner has one
  const handleLogoClick = useCallback(() => {
    if (hasWelcomePopupData) {
      setWelcomePopupManualOpen(true)
    }
  }, [hasWelcomePopupData])

  const [isModalOpen, setIsModalOpen] = useState<boolean>(!!props.joinModalParams)

  // Set Partner Cookie when set via Partner auth token
  useEffect(() => {
    if (!router.isReady) return
    ;(async () => {
      try {
        if (
          props.slug.toLowerCase() === publicRuntimeConfig.partnerIntegrations.gale.partnerSlug.toLowerCase() &&
          router.query['id_token']
        ) {
          await queryGsuApi(QUERY_SET_PARTNER_METADATA, {
            input: {
              partnerSlug: props.slug,
              jwtToken: router.query['id_token'],
            },
          })
          setCheckAndCreatePartnerAssociation(true)
          router.push('/partner/gale')
        }
      } catch (error) {}
    })()
  }, [router.isReady])

  // Test feature: ?testwelcome=1 forces the welcome popup to display
  // Sets the signup-entitlements cookie and reloads without the query param
  useEffect(() => {
    if (!router.isReady || !initialPageData?.page) return
    if (router.query['testwelcome'] !== '1') return

    const partnerId = getPartnerIdFromPageSections(initialPageData.page)
    if (!partnerId) return

    // Set the signup-entitlements cookie
    const cookieValue = encodeURIComponent(JSON.stringify([{ partnerId, type: 'partner' }]))
    const domain = window.location.hostname.includes('getsetup.io')
      ? '.www.getsetup.io'
      : window.location.hostname.includes('gsudevelopment.com')
      ? '.gsudevelopment.com'
      : window.location.hostname
    document.cookie = `auth.signup-entitlements=${cookieValue}; domain=${domain}; path=/; max-age=60`

    // Remove the query param and reload
    const url = new URL(window.location.href)
    url.searchParams.delete('testwelcome')
    window.location.href = url.toString()
  }, [router.isReady, router.query, initialPageData])

  // Assign for typing and in-case of no response
  useEffect(() => {
    if (initialPageData && !canSeeHomeAdditionalCategoryRows) {
      setPageData({
        ...initialPageData.page,
      })
    }
    if (initialPageData && extraPageData && canSeeHomeAdditionalCategoryRows) {
      const insertAtIndex = 6
      const combinedSections = [...initialPageData.page.sections]
      combinedSections.splice(insertAtIndex, 0, ...extraPageData.sections.sections)
      setPageData({
        ...initialPageData.page,
        sections: combinedSections,
      })
    }
  }, [initialPageData, extraPageData, canSeeHomeAdditionalCategoryRows])

  // Find the Partner section which has the data we need for the partner modal
  const partner: PartnerInfoPageSection | undefined =
    pageData && pageData.sections && pageData.sections.find(isPartnerInfoSection)

  // Partner integrations
  useEffect(() => {
    if (!props.authToken || !pageData) return

    const currentPagePartnerId = getPartnerIdFromPageSections(pageData)
    const galePartner = publicRuntimeConfig.partnerIntegrations.gale
    if (!currentPagePartnerId || currentPagePartnerId !== galePartner.partnerId) return
    ;(async () => {
      try {
        // check partner association
        const response = await axios({
          method: 'get',
          baseURL: publicRuntimeConfig.webapi.url,
          headers: { authorization: props.authToken },
          url: `/api/user/me/partner-association/${galePartner.partnerSlug}`,
        })
        if (!response.data.partner) {
          throw new Error('Partner association not found')
        }
        const metadata = response.data.partner.metadata ? JSON.parse(response.data.partner.metadata) : null
        if (!metadata && checkAndCreatePartnerAssociation) {
          const metadataValue = getPartnerMetadataFromCookieUsingPartnerId(galePartner.partnerId)
          try {
            await axios({
              method: 'put',
              baseURL: publicRuntimeConfig.webapi.url,
              headers: { authorization: props.authToken },
              url: `/api/user/me/partner-association/${galePartner.partnerSlug}`,
              data: {
                metadata: metadataValue,
              },
            })
          } catch (error) {
            console.error(error)
          }
          return
        }
        if (
          !metadata ||
          galePartner.requiredMetadataParams.length !==
            intersection(keysIn(metadata), galePartner.requiredMetadataParams).length
        ) {
          setAccessRestrictedAuthDialog(true)
        }
      } catch (err) {
        console.error(err)
        if (!checkAndCreatePartnerAssociation) {
          setAccessRestrictedAuthDialog(true)
          return
        }
        try {
          const metadataValue = getPartnerMetadataFromCookieUsingPartnerId(galePartner.partnerId)
          // create partner association
          await axios({
            method: 'post',
            baseURL: publicRuntimeConfig.webapi.url,
            headers: { authorization: props.authToken },
            url: `/api/user/me/partner-association`,
            data: {
              partnerSlug: galePartner.partnerSlug,
              metadata: metadataValue,
            },
          })
        } catch (error) {}
      }
    })()
  }, [props.authToken, pageData, checkAndCreatePartnerAssociation])

  if (showAccessRestrictedDialog)
    return (
      <PageLayout>
        <AccessRestrictedDialog />
      </PageLayout>
    )

  return (
    <>
      <PageLayout>
        {pageData && <PageMetaInfo {...pageData?.metaInfo} />}
        {pageData?.onLoadAction ? <HandleOnLoadAction {...pageData?.onLoadAction} /> : null}
        <PageComposerWithLoader
          {...pageData}
          excludeSections={['FeaturedClassesSection']}
          fetching={fetching || fetchingExtras}
          partnerId={props.partnerId}
          onLogoClick={hasWelcomePopupData ? handleLogoClick : undefined}
        />
      </PageLayout>
      {partner && props.joinModalParams?.step === JoinModalSteps.init ? (
        <Modal isOpen={isModalOpen} isClosable={false} ariaLabel={t('partner:authModal.ariaLabel.init')}>
          <WelcomeContent
            partnerLogo={partner.content.logo}
            partnerName={partner.content.name}
            url={props.partnerAuthURL ? props.partnerAuthURL : undefined}
          />
        </Modal>
      ) : null}
      {/* Welcome popup - first-time visitor (localStorage-tracked) or manually opened via logo */}
      {hasWelcomePopupData ? (
        <Modal
          isOpen={showFirstVisitPopup || welcomePopupManualOpen}
          onDismiss={() => {
            dismissFirstVisit()
            setWelcomePopupManualOpen(false)
          }}
          className={welcomePopup?.videoAsset?.playbackUrlMP4 ? 'p-0' : undefined}
        >
          <WelcomePopupContent
            title={welcomePopup!.title || undefined}
            description={welcomePopup!.description || undefined}
            videoAsset={welcomePopup!.videoAsset || undefined}
            videoPoster={welcomePopup!.videoPoster || undefined}
            partnerLogo={partner?.content.logo}
            partnerName={partner?.content.name}
            onDismiss={() => {
              dismissFirstVisit()
              setWelcomePopupManualOpen(false)
            }}
          />
        </Modal>
      ) : null}
      <UserPreferencesWrapper />
    </>
  )
}

// For certain partners, we have branded 'sales' pages that
// we want to redirect to when a user is unauthenticated.
const unauthenticatedPartnerPageMapping: Record<string, string> = {
  saga: '/saga/dig',
  'saga-digital': '/saga/dig',
  'saga-magazine': '/saga/mag',
  'saga-experienced-voices': '/saga/exp',
}

// Partner pages have some bespoke query parameter which trigger a modal experience for users. This has
// been built for Tivity/Silversneakers integration, but can be adapted to suit other possibilities.
// Meanings of these query parameters:
// ref = This join modal is only for Tivity integration currently. Used to look up whether to show or not
// step = Step in the modal experience
// simulate & account = Combined with account, means we can pretend to go through the experience impersonating a specific users account
export const getServerSideProps: GetServerSideProps = async ({
  locale = 'en-US',
  req,
  res,
  query: { slug, ref, step, simulate, account, utm_classid, utm_classname, utm_category, utm_series },
}) => {
  let disableAuthPrompt = true
  let persistentAuthPrompt = false
  let delayAuthPrompt = null

  // Show persistent auth prompt on the Gale partner page for the non-authenticated users
  if (slug === 'gale') {
    disableAuthPrompt = false
    persistentAuthPrompt = true
    delayAuthPrompt = 0
  }

  const [urqlState, statusCode, gqlData] = await queryGsuApi<{ currentUser: CurrentUser; page: Page | null }>(
    QUERY_PARTNER,
    { slug },
    req,
  )

  // Let's 404 if there's no Partner with the passed slug.
  if (gqlData?.page === null) {
    res.statusCode = statusCode ?? 404
    return {
      notFound: true,
    }
  }

  const _slug = Array.isArray(slug) ? slug[0] : slug

  // We're not authenticated, so check if we need to redirect away
  // from this page, back to our partner sales page.
  if (!gqlData?.currentUser.authenticatedUser?.id) {
    const unauthenticatedPartnerPage = _slug && unauthenticatedPartnerPageMapping[_slug]

    if (unauthenticatedPartnerPage) {
      return {
        redirect: {
          destination: unauthenticatedPartnerPage,
          permanent: false,
        },
      }
    }
  }

  // If the authenticated user is accessing the base catalog partner page,
  // check if they have a current partner association other than the default.
  // If so, redirect them to that partner's landing page.
  if (_slug === publicRuntimeConfig.baseCatalogPartnerSlug && gqlData?.currentUser.authenticatedUser?.id) {
    const [, , partnerAssocData] = await queryGsuApi<{ currentUser: CurrentUser }>(QUERY_PARTNER_ASSOCIATIONS, {}, req)

    const associations = partnerAssocData?.currentUser?.authenticatedUser?.partnerAssociations
    if (associations && associations.length > 0) {
      const currentAssociation = associations.find((assoc) => assoc?.isPrimary === true && assoc?.active !== false)

      if (
        currentAssociation &&
        currentAssociation.partner.id !== publicRuntimeConfig.baseCatalogPartnerId &&
        currentAssociation.partner.slug !== _slug
      ) {
        return {
          redirect: {
            destination: `/partner/${currentAssociation.partner.slug}`,
            permanent: false,
          },
        }
      }
    }
  }

  // By default in Next.JS the param is `undefined` if not set. This is an issue
  // for Next.JS serialization. Let's clean this up here by removing properties with `undefined` + `null` as values
  const joinModalParams = omitBy(
    {
      slug,
      ref,
      step,
      simulate: simulate ? simulate === 'true' : undefined,
      account,
      utm_classid,
      utm_classname,
      utm_category,
      utm_series,
    }, // simulate here must be converted to a boolean if set
    isNil,
  )

  // TODO: This will be re-worked once auth + paywall modals are done
  // Only supporting tivity for now
  const partnerAuthBaseURL =
    ref === JoinModalAllowedPartnerRefs.tivity ? publicRuntimeConfig.integrations.tivity.url : undefined

  // Construct the full url
  const partnerAuthURL = partnerAuthBaseURL ? new URL(partnerAuthBaseURL) : undefined
  map(joinModalParams, function (value, key) {
    partnerAuthURL?.searchParams.set(key, toString(value))
  })
  const partnerState = getPartnerAttributionStateFromCookies(req.cookies)
  const { authLocalCookie, authFacebookCookie } = getClientIdentityForQuery(req)

  return {
    props: {
      ...(await serverSideTranslations(locale, ['badge', 'common', 'classes', 'partner', 'series'])),
      slug,
      joinModalParams: s.is(joinModalParams, JoinModalParams) ? joinModalParams : null,
      partnerAuthURL: partnerAuthURL ? partnerAuthURL.toString() : null,
      urqlState,
      disableAuthPrompt,
      persistentAuthPrompt,
      delayAuthPrompt,
      partnerId: partnerState ? partnerState.id : null,
      authToken: authLocalCookie || authFacebookCookie,
    },
  }
}

export default Partner
